import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Store, Plus, Settings, Trash2, ExternalLink, Eye, EyeOff } from 'lucide-react';

const ShopsList = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shopToDelete, setShopToDelete] = useState(null);
  const [newShopName, setNewShopName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      const response = await api.get('/shops');
      setShops(response.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
      toast.error('Erreur lors du chargement des boutiques');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShop = async (e) => {
    e.preventDefault();
    if (!newShopName.trim()) {
      toast.error('Veuillez entrer un nom');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/shops', { name: newShopName });
      setShops([...shops, response.data]);
      setCreateDialogOpen(false);
      setNewShopName('');
      toast.success('Boutique créée !');
      navigate(`/app/shops/${response.data.id}/settings`);
    } catch (error) {
      console.error('Error creating shop:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteShop = async () => {
    if (!shopToDelete) return;

    try {
      await api.delete(`/shops/${shopToDelete.id}`);
      setShops(shops.filter(s => s.id !== shopToDelete.id));
      toast.success('Boutique supprimée');
    } catch (error) {
      console.error('Error deleting shop:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setShopToDelete(null);
    }
  };

  const togglePublish = async (shop) => {
    try {
      const response = await api.put(`/shops/${shop.id}`, { isPublished: !shop.isPublished });
      setShops(shops.map(s => s.id === shop.id ? response.data : s));
      toast.success(response.data.isPublished ? 'Catalogue publié !' : 'Catalogue dépublié');
    } catch (error) {
      console.error('Error toggling publish:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shops-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Mes boutiques</h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos catalogues digitaux
          </p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="create-shop-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle boutique
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer une boutique</DialogTitle>
              <DialogDescription>
                Donnez un nom à votre nouvelle boutique
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateShop} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="shopName">Nom de la boutique</Label>
                <Input
                  id="shopName"
                  placeholder="Ma Boutique"
                  value={newShopName}
                  onChange={(e) => setNewShopName(e.target.value)}
                  data-testid="new-shop-name"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={creating} data-testid="confirm-create-shop">
                  {creating ? 'Création...' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Shops list */}
      {shops.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucune boutique</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Créez votre première boutique pour commencer à construire votre catalogue digital
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Créer ma première boutique
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shops.map((shop) => (
            <Card key={shop.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {shop.logoUrl ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${shop.logoUrl}`}
                        alt={shop.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Store className="w-6 h-6 text-primary" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg">{shop.name}</CardTitle>
                      <CardDescription className="text-xs">
                        /{shop.slug}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Statut</span>
                  <span className={shop.isPublished ? 'text-green-600' : 'text-yellow-600'}>
                    {shop.isPublished ? 'Publié' : 'Brouillon'}
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/app/shops/${shop.id}/settings`)}
                    data-testid={`shop-settings-${shop.id}`}
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Gérer
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => togglePublish(shop)}
                    data-testid={`shop-toggle-${shop.id}`}
                  >
                    {shop.isPublished ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Masquer
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        Publier
                      </>
                    )}
                  </Button>
                  
                  {shop.isPublished && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/s/${shop.slug}`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Voir
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      setShopToDelete(shop);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`shop-delete-${shop.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la boutique ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Toutes les catégories, produits et données associées seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteShop}
              className="bg-destructive hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ShopsList;
